import type { TraderProfile as MesaTraderProfile } from '@/types/api/legacy-trader-profile'
import type { MPID } from '@/types/enums/venues'

import { Badge } from '@moment/design-system-ui/badge'
import { Tooltip } from '@moment/design-system-ui/tooltip'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import {
  Table,
  TableBody,
  TableRow,
} from '@/components/atomic/table-basic-legacy'
import { BodySubtext, BodyXS } from '@/components/atomic/text'
import { getVenueLabel } from '@/trading/helpers/venues'
import { OrderSide } from '@/types/enums/order'
import { formatBps } from '@/utils/presentation/bonds'
import {
  formatLargeNumbers,
  formatMoney,
  formatNumber,
  formatPercentage,
  PLACEHOLDER,
} from '@/utils/presentation/formatting'
import { cn } from '@/utils/shadcn/utils'

import ActionColumn from './action-column'
import { AnimatedPanel } from './animated-panel'
import styles from './order-book.module.css'
import {
  DisabledCell,
  DisabledRow,
  HighlightAskCell,
  HighlightAskRow,
  HighlightBidCell,
  HighlightBidRow,
  OrderBookCell,
  OrderBookTableContainer,
  OrderBookTableHeader,
} from './styles'
import {
  ActivePanelType,
  type EnrichedOrderBookRowItem,
  type OrderBookTablesProps,
  QuoteSide,
  QuoteSource,
} from './types'
import { formatOrderBookQuotePrice, getRowHeight } from './utils'

const MemoizedHighlightBidRow = React.memo(HighlightBidRow)
MemoizedHighlightBidRow.displayName = 'MemoizedHighlightBidRow'

const MemoizedHighlightBidCell = React.memo(HighlightBidCell)
MemoizedHighlightBidCell.displayName = 'MemoizedHighlightBidCell'

const MemoizedHighlightAskRow = React.memo(HighlightAskRow)
MemoizedHighlightAskRow.displayName = 'MemoizedHighlightAskRow'

const MemoizedHighlightAskCell = React.memo(HighlightAskCell)
MemoizedHighlightAskCell.displayName = 'MemoizedHighlightAskCell'

const MemoizedDisabledRow = React.memo(DisabledRow)
MemoizedDisabledRow.displayName = 'MemoizedDisabledRow'

const MemoizedDisabledCell = React.memo(DisabledCell)
MemoizedDisabledCell.displayName = 'MemoizedDisabledCell'

const MemoizedTableRow = React.memo(TableRow)
MemoizedTableRow.displayName = 'MemoizedTableRow'

const MemoizedOrderBookCell = React.memo(OrderBookCell)
MemoizedOrderBookCell.displayName = 'MemoizedOrderBookCell'

export function OrderBookTables({
  activePanel,
  bids,
  asks,
  orders,
  rows,
  isCollapsed = false,
  onRowAction,
  isButtonDisabled = false,
  showVenuesColumn = false,
  showSource = false,
  showMbrAndIncrement = false,
  benchmarkIsin,
  orderSide,
  showActionButtonsOnAllRows = false,
  hideActionButtonsForNonApplicableTradeSide = false,
  showPreAndPostFeeValues = false,
  isQuoteSnapshot = false,
}: OrderBookTablesProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const allBids = orders.filter((order) => order.bidPrice !== null)
  const allAsks = orders.filter((order) => order.askPrice !== null)

  const [loadingState, setLoadingState] = useState<{
    side?: QuoteSide
    index?: number
    isLoading: boolean
  }>({ isLoading: false, index: -1 })

  const SCROLL_TIMER_DELAY = {
    COLLAPSE: 0,
    EXPAND: 285,
  }

  useEffect(() => {
    const scrollTimer = setTimeout(
      () => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: isCollapsed ? containerRef.current.scrollHeight : 0,
            behavior: 'smooth',
          })
        }
      },
      isCollapsed ? SCROLL_TIMER_DELAY.COLLAPSE : SCROLL_TIMER_DELAY.EXPAND,
    )

    return () => clearTimeout(scrollTimer)
  }, [isCollapsed, SCROLL_TIMER_DELAY.COLLAPSE, SCROLL_TIMER_DELAY.EXPAND])

  const hasMoreRows =
    rows !== undefined && (allBids.length > rows || allAsks.length > rows)

  const showActionColumn = !!onRowAction
  const showActionColumnForBids =
    showActionColumn &&
    (hideActionButtonsForNonApplicableTradeSide
      ? orderSide === OrderSide.Sell && activePanel === ActivePanelType.Bid
      : // For example, SOT does not hide bids for the non-applicable trade side
        true)
  const showActionColumnForAsks =
    showActionColumn &&
    (hideActionButtonsForNonApplicableTradeSide
      ? orderSide === OrderSide.Buy && activePanel === ActivePanelType.Ask
      : // For example, SOT does not hide asks for the non-applicable trade side
        true)
  const shouldShowActionColumnForBidsOnLeft =
    activePanel === ActivePanelType.Both &&
    !hideActionButtonsForNonApplicableTradeSide

  const showSpreadToBenchmarkForBids = allBids.some(
    (bid) => !!bid.bidSpreadToBenchmarkBps,
  )
  const showSpreadToBenchmarkForAsks = allAsks.some(
    (ask) => !!ask.askSpreadToBenchmarkBps,
  )
  const showSpreadToBenchmark =
    (showSpreadToBenchmarkForBids || showSpreadToBenchmarkForAsks) &&
    !(activePanel === ActivePanelType.Both && isQuoteSnapshot) // Don't show spread on combined panel when showing snapshot

  const showYieldToWorst = !(
    activePanel === ActivePanelType.Both && isQuoteSnapshot
  ) // Don't show yield to worst on combined panel when showing snapshot

  const getColorClasses = (type: ActivePanelType.Bid | ActivePanelType.Ask) => {
    return type === ActivePanelType.Bid
      ? 'text-moment-red-600 dark:text-moment-red-300'
      : 'text-moment-green-800 dark:text-moment-green-800'
  }

  const firstValidBid = bids.findIndex((bid) => !bid.disabled)
  const firstValidAsk = asks.findIndex((ask) => !ask.disabled)

  const hasExplicitBidHighlight = bids.some((bid) => bid.highlighted)
  const hasExplicitAskHighlight = asks.some((ask) => ask.highlighted)

  const rowHeight = getRowHeight(showPreAndPostFeeValues)

  const showBoth = activePanel === ActivePanelType.Both

  const handleHitBid = useCallback(
    (idx: number, orderBookRow: EnrichedOrderBookRowItem) => {
      const onHitBid = async (
        traderProfile: MesaTraderProfile,
        idx: number,
        orderBookRow: EnrichedOrderBookRowItem,
      ) => {
        setLoadingState({
          side: QuoteSide.Bid,
          isLoading: true,
          index: idx,
        })
        await onRowAction?.(orderBookRow, QuoteSide.Bid, traderProfile)
        setLoadingState({
          side: QuoteSide.Bid,
          isLoading: false,
          index: -1,
        })
      }
      return (traderProfile: MesaTraderProfile) =>
        onHitBid(traderProfile, idx, orderBookRow)
    },
    [onRowAction],
  )

  const handleLiftAsk = useCallback(
    (idx: number, orderBookRow: EnrichedOrderBookRowItem) => {
      const onLiftAsk = async (
        traderProfile: MesaTraderProfile,
        idx: number,
        orderBookRow: EnrichedOrderBookRowItem,
      ) => {
        setLoadingState({
          side: QuoteSide.Ask,
          isLoading: true,
          index: idx,
        })
        await onRowAction?.(orderBookRow, QuoteSide.Ask, traderProfile)
        setLoadingState({
          side: QuoteSide.Ask,
          isLoading: false,
          index: -1,
        })
      }
      return (traderProfile: MesaTraderProfile) =>
        onLiftAsk(traderProfile, idx, orderBookRow)
    },
    [onRowAction],
  )

  return (
    <OrderBookTableContainer
      ref={containerRef}
      rows={isCollapsed ? 0 : rows}
      showBoth={showBoth}
      className={`${hasMoreRows && !isCollapsed && styles['order-book-gradient']}`}
      isCollapsed={isCollapsed}
      showPreAndPostFeeValues={showPreAndPostFeeValues}
    >
      {/* Bids Table */}
      <AnimatedPanel panelType={ActivePanelType.Bid} activePanel={activePanel}>
        <Table
          className={cn(
            'relative min-w-full divide-y grow w-full h-fit border-separate border-spacing-0',
          )}
        >
          <OrderBookTableHeader
            isAsks={false}
            showActionColumn={showActionColumnForBids}
            showVenuesColumn={showVenuesColumn}
            showSpreadToBenchmark={showSpreadToBenchmark}
            showSource={showSource}
            showYieldToWorst={showYieldToWorst}
            showMbrAndIncrement={showMbrAndIncrement}
            benchmarkIsin={benchmarkIsin}
            isActivePanelBoth={showBoth}
          />
          <TableBody>
            {bids.map((orderBookRow, idx) => {
              const borderStatus = idx !== bids.length - 1
              const isHighlight = hasExplicitBidHighlight
                ? orderBookRow.highlighted
                : idx === 0 || idx === firstValidBid
              const isDisabled = orderBookRow.disabled
              const isFirstValidBid = firstValidBid === idx

              const RowComponent = isDisabled
                ? MemoizedDisabledRow
                : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  isHighlight
                  ? MemoizedHighlightBidRow
                  : MemoizedTableRow

              const CellComponent = isDisabled
                ? MemoizedDisabledCell
                : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  isHighlight
                  ? MemoizedHighlightBidCell
                  : MemoizedOrderBookCell

              const isButtonLoading =
                loadingState.isLoading &&
                loadingState.side === QuoteSide.Bid &&
                loadingState.index === idx

              return (
                <RowComponent
                  key={`${orderBookRow.bidPrice}-${idx}`}
                  className="group"
                >
                  {/* Action column on left */}
                  {showActionColumnForBids &&
                    shouldShowActionColumnForBidsOnLeft && (
                      <ActionColumn
                        orderBookRow={orderBookRow}
                        isDisabled={isDisabled}
                        borderStatus={borderStatus}
                        isHighlight={isHighlight}
                        rowHeight={rowHeight}
                        isButtonDisabled={
                          isButtonDisabled || loadingState.isLoading
                        }
                        loadingState={{
                          isLoading: isButtonLoading,
                          index: loadingState.index,
                        }}
                        showActionButtonsOnAllRows={showActionButtonsOnAllRows}
                        isFirstValid={isFirstValidBid}
                        renderOnLeft={true}
                        CellComponent={CellComponent}
                        onAction={handleHitBid}
                        idx={idx}
                        quoteSide={QuoteSide.Bid}
                      />
                    )}
                  {/* Source column */}
                  {showSource && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-right"
                      height={rowHeight}
                    >
                      <div className="flex flex-col items-end w-full">
                        {orderBookRow.bidSource ? (
                          <Badge
                            leftSection={
                              <Icon
                                iconName={
                                  orderBookRow.bidSource === QuoteSource.RFQ
                                    ? IconName.InboxIcon
                                    : IconName.BookOpenIcon
                                }
                                size="medium"
                              />
                            }
                          >
                            {orderBookRow.bidSource}
                          </Badge>
                        ) : (
                          <BodySubtext>{PLACEHOLDER}</BodySubtext>
                        )}
                      </div>
                    </CellComponent>
                  )}
                  {/* Venues column */}
                  {showVenuesColumn && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-right"
                      height={rowHeight}
                    >
                      {getVenueLabel(orderBookRow.bidVenue as MPID) ??
                        PLACEHOLDER}
                    </CellComponent>
                  )}
                  {/* Spread to benchmark column */}
                  {showSpreadToBenchmark && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-right"
                      height={rowHeight}
                    >
                      {typeof orderBookRow.bidSpreadToBenchmarkBps ===
                        'object' && (
                        <div className="flex flex-col">
                          <BodySubtext>
                            {formatBps(
                              Number(
                                orderBookRow.bidSpreadToBenchmarkBps
                                  ?.preFeeSpreadBps,
                              ),
                            )}
                          </BodySubtext>
                          <BodyXS className="text-secondary">
                            {formatBps(
                              Number(
                                orderBookRow.bidSpreadToBenchmarkBps
                                  ?.postFeeSpreadBps,
                              ),
                            )}
                          </BodyXS>
                        </div>
                      )}
                      {typeof orderBookRow.bidSpreadToBenchmarkBps !==
                        'object' &&
                        formatBps(Number(orderBookRow.bidSpreadToBenchmarkBps))}
                    </CellComponent>
                  )}
                  {/* Min num bonds column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className="text-right"
                    height={rowHeight}
                  >
                    <div className="flex flex-col">
                      <span>
                        {formatLargeNumbers(
                          formatNumber(orderBookRow.bidMinNumBonds, {
                            useZeroPlaceholder: true,
                          }),
                        )}
                      </span>
                      {showMbrAndIncrement &&
                        orderBookRow.bidMinIncrement !== null && (
                          <BodyXS className="text-secondary">
                            Inc{' '}
                            {formatLargeNumbers(
                              formatNumber(orderBookRow.bidMinIncrement),
                            )}
                          </BodyXS>
                        )}
                    </div>
                  </CellComponent>
                  {/* Num bonds column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className="text-right"
                    height={rowHeight}
                  >
                    <div className="flex flex-col">
                      <span>
                        {formatLargeNumbers(
                          formatNumber(orderBookRow.bidNumBonds),
                        )}
                      </span>
                      {showMbrAndIncrement &&
                        orderBookRow.bidMinBalanceRemaining !== null && (
                          <BodyXS className="text-secondary">
                            MBR{' '}
                            {formatLargeNumbers(
                              formatNumber(orderBookRow.bidMinBalanceRemaining),
                            )}
                          </BodyXS>
                        )}
                    </div>
                  </CellComponent>
                  {/* Yield to worst column */}
                  {showYieldToWorst && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-right"
                      height={rowHeight}
                    >
                      {typeof orderBookRow.bidYieldToWorst === 'object' && (
                        <div className="flex flex-col">
                          <BodySubtext>
                            {formatPercentage(
                              orderBookRow.bidYieldToWorst?.preFeeYtw ?? null,
                              {
                                numDigits: 3,
                              },
                            )}
                          </BodySubtext>
                          <BodyXS className="text-secondary">
                            {formatPercentage(
                              orderBookRow.bidYieldToWorst?.postFeeYtw ?? null,
                              {
                                numDigits: 3,
                              },
                            )}
                          </BodyXS>
                        </div>
                      )}
                      {typeof orderBookRow.bidYieldToWorst !== 'object' &&
                        formatPercentage(orderBookRow.bidYieldToWorst, {
                          numDigits: 3,
                        })}
                    </CellComponent>
                  )}
                  {/* Price column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className={cn(
                      !isDisabled && getColorClasses(ActivePanelType.Bid),
                      'text-right',
                    )}
                    height={rowHeight}
                  >
                    {typeof orderBookRow.bidPrice === 'object' && (
                      <div className="flex flex-col">
                        <BodySubtext>
                          {formatOrderBookQuotePrice(
                            orderBookRow.bidPrice?.preFeePrice,
                            OrderSide.Sell,
                          )}
                        </BodySubtext>
                        <BodyXS className="text-secondary">
                          {formatOrderBookQuotePrice(
                            orderBookRow.bidPrice?.postFeePrice,
                            OrderSide.Sell,
                          )}
                        </BodyXS>
                      </div>
                    )}
                    {typeof orderBookRow.bidPrice !== 'object' &&
                      formatOrderBookQuotePrice(
                        orderBookRow.bidPrice,
                        OrderSide.Sell,
                      )}
                  </CellComponent>
                  {/* Action column on right */}
                  {showActionColumnForBids &&
                    !shouldShowActionColumnForBidsOnLeft && (
                      <ActionColumn
                        orderBookRow={orderBookRow}
                        isDisabled={isDisabled}
                        borderStatus={borderStatus}
                        isHighlight={isHighlight}
                        rowHeight={rowHeight}
                        isButtonDisabled={
                          isButtonDisabled || loadingState.isLoading
                        }
                        loadingState={{
                          isLoading: isButtonLoading,
                          index: loadingState.index,
                        }}
                        showActionButtonsOnAllRows={showActionButtonsOnAllRows}
                        isFirstValid={isFirstValidBid}
                        renderOnLeft={false}
                        CellComponent={CellComponent}
                        onAction={handleHitBid}
                        idx={idx}
                        quoteSide={QuoteSide.Bid}
                      />
                    )}
                </RowComponent>
              )
            })}
          </TableBody>
        </Table>
      </AnimatedPanel>

      {/* Asks Table */}
      <AnimatedPanel panelType={ActivePanelType.Ask} activePanel={activePanel}>
        <Table
          className={cn(
            'relative flex-col w-full h-fit border-separate border-spacing-0',
          )}
        >
          <OrderBookTableHeader
            isAsks={true}
            showActionColumn={showActionColumnForAsks}
            showVenuesColumn={showVenuesColumn}
            showSpreadToBenchmark={showSpreadToBenchmark}
            showSource={showSource}
            showYieldToWorst={showYieldToWorst}
            showMbrAndIncrement={showMbrAndIncrement}
            benchmarkIsin={benchmarkIsin}
            isActivePanelBoth={showBoth}
          />
          <TableBody>
            {asks.map((orderBookRow, idx) => {
              const borderStatus = idx !== asks.length - 1
              const isHighlight = hasExplicitAskHighlight
                ? orderBookRow.highlighted
                : idx === 0 || idx === firstValidAsk
              const isDisabled = orderBookRow.disabled
              const isFirstValidAsk = firstValidAsk === idx

              const RowComponent = isDisabled
                ? MemoizedTableRow
                : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  isHighlight
                  ? MemoizedHighlightAskRow
                  : MemoizedTableRow

              const CellComponent = isDisabled
                ? MemoizedDisabledCell
                : // biome-ignore lint/style/noNestedTernary: Existing code — enable rule without runtime changes
                  isHighlight
                  ? MemoizedHighlightAskCell
                  : MemoizedOrderBookCell

              const isButtonLoading =
                loadingState.isLoading &&
                loadingState.side === QuoteSide.Ask &&
                loadingState.index === idx

              const askAdvisorConcession = orderBookRow.askAdvisorConcession
              const advisorConcessionLabel =
                askAdvisorConcession != null
                  ? `Advisor concession: ${formatMoney(askAdvisorConcession, {
                      numDigits: 2,
                      addTrailingZeros: true,
                    })}`
                  : null

              const askPriceInner =
                typeof orderBookRow.askPrice === 'object' ? (
                  <div className="flex flex-col">
                    <BodySubtext>
                      {formatOrderBookQuotePrice(
                        orderBookRow.askPrice?.preFeePrice,
                        OrderSide.Buy,
                      )}
                    </BodySubtext>
                    <BodyXS className="text-secondary">
                      {formatOrderBookQuotePrice(
                        orderBookRow.askPrice?.postFeePrice,
                        OrderSide.Buy,
                      )}
                    </BodyXS>
                  </div>
                ) : (
                  formatOrderBookQuotePrice(
                    orderBookRow.askPrice,
                    OrderSide.Buy,
                  )
                )

              const askPriceContent = advisorConcessionLabel ? (
                <Tooltip content={advisorConcessionLabel}>
                  <div className="w-fit underline decoration-dotted underline-offset-2 cursor-help">
                    {askPriceInner}
                  </div>
                </Tooltip>
              ) : (
                askPriceInner
              )

              return (
                <RowComponent
                  key={`${orderBookRow.askPrice}-${idx}`}
                  tabIndex={0}
                  className="group"
                >
                  {/* Price column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className={cn(
                      !isDisabled && getColorClasses(ActivePanelType.Ask),
                      'text-left',
                    )}
                    height={rowHeight}
                  >
                    {askPriceContent}
                  </CellComponent>

                  {/* Yield to worst column */}
                  {showYieldToWorst && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-left"
                      height={rowHeight}
                    >
                      {typeof orderBookRow.askYieldToWorst === 'object' && (
                        <div className="flex flex-col">
                          <BodySubtext>
                            {formatPercentage(
                              orderBookRow.askYieldToWorst?.preFeeYtw ?? null,
                              {
                                numDigits: 3,
                              },
                            )}
                          </BodySubtext>
                          <BodyXS className="text-secondary">
                            {formatPercentage(
                              orderBookRow.askYieldToWorst?.postFeeYtw ?? null,
                              {
                                numDigits: 3,
                              },
                            )}
                          </BodyXS>
                        </div>
                      )}
                      {typeof orderBookRow.askYieldToWorst !== 'object' &&
                        formatPercentage(orderBookRow.askYieldToWorst, {
                          numDigits: 3,
                        })}
                    </CellComponent>
                  )}
                  {/* Num bonds column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className="text-left"
                    height={rowHeight}
                  >
                    <div className="flex flex-col">
                      <span>
                        {formatLargeNumbers(
                          formatNumber(orderBookRow.askNumBonds),
                        )}
                      </span>
                      {showMbrAndIncrement &&
                        orderBookRow.askMinBalanceRemaining !== null && (
                          <BodyXS className="text-secondary">
                            MBR{' '}
                            {formatLargeNumbers(
                              formatNumber(orderBookRow.askMinBalanceRemaining),
                            )}
                          </BodyXS>
                        )}
                    </div>
                  </CellComponent>
                  {/* Min num bonds column */}
                  <CellComponent
                    hasBorder={(!isHighlight || isDisabled) && borderStatus}
                    className="text-left"
                    height={rowHeight}
                  >
                    <div className="flex flex-col">
                      <span>
                        {formatLargeNumbers(
                          formatNumber(orderBookRow?.askMinNumBonds, {
                            useZeroPlaceholder: true,
                          }),
                        )}
                      </span>
                      {showMbrAndIncrement &&
                        orderBookRow.askMinIncrement !== null && (
                          <BodyXS className="text-secondary">
                            Inc{' '}
                            {formatLargeNumbers(
                              formatNumber(orderBookRow.askMinIncrement),
                            )}
                          </BodyXS>
                        )}
                    </div>
                  </CellComponent>
                  {/* Spread to benchmark column */}
                  {showSpreadToBenchmark && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-left"
                      height={rowHeight}
                    >
                      {typeof orderBookRow.askSpreadToBenchmarkBps ===
                        'object' && (
                        <div className="flex flex-col">
                          <BodySubtext>
                            {formatBps(
                              Number(
                                orderBookRow.askSpreadToBenchmarkBps
                                  ?.preFeeSpreadBps,
                              ),
                            )}
                          </BodySubtext>
                          <BodyXS className="text-secondary">
                            {formatBps(
                              Number(
                                orderBookRow.askSpreadToBenchmarkBps
                                  ?.postFeeSpreadBps,
                              ),
                            )}
                          </BodyXS>
                        </div>
                      )}
                      {typeof orderBookRow.askSpreadToBenchmarkBps !==
                        'object' &&
                        formatBps(Number(orderBookRow.askSpreadToBenchmarkBps))}
                    </CellComponent>
                  )}
                  {/* Venues column */}
                  {showVenuesColumn && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-left"
                      height={rowHeight}
                    >
                      {getVenueLabel(orderBookRow.askVenue as MPID) ??
                        PLACEHOLDER}
                    </CellComponent>
                  )}
                  {/* Source column */}
                  {showSource && (
                    <CellComponent
                      hasBorder={(!isHighlight || isDisabled) && borderStatus}
                      className="text-left"
                      height={rowHeight}
                    >
                      <div className="flex flex-col items-start w-full">
                        {orderBookRow.askSource ? (
                          <Badge
                            leftSection={
                              <Icon
                                iconName={
                                  orderBookRow.askSource === QuoteSource.RFQ
                                    ? IconName.InboxIcon
                                    : IconName.BookOpenIcon
                                }
                                size="medium"
                              />
                            }
                          >
                            {orderBookRow.askSource}
                          </Badge>
                        ) : (
                          <BodySubtext>{PLACEHOLDER}</BodySubtext>
                        )}
                      </div>
                    </CellComponent>
                  )}
                  {/* Action column on right */}
                  {showActionColumnForAsks && (
                    <ActionColumn
                      orderBookRow={orderBookRow}
                      isDisabled={isDisabled}
                      borderStatus={borderStatus}
                      isHighlight={isHighlight}
                      rowHeight={rowHeight}
                      isButtonDisabled={
                        isButtonDisabled || loadingState.isLoading
                      }
                      loadingState={{
                        isLoading: isButtonLoading,
                        index: loadingState.index,
                      }}
                      showActionButtonsOnAllRows={showActionButtonsOnAllRows}
                      isFirstValid={isFirstValidAsk}
                      CellComponent={CellComponent}
                      onAction={handleLiftAsk}
                      idx={idx}
                      quoteSide={QuoteSide.Ask}
                    />
                  )}
                </RowComponent>
              )
            })}
          </TableBody>
        </Table>
      </AnimatedPanel>
    </OrderBookTableContainer>
  )
}
